// Centralized verbatim copy for the "Built with AI" (Survey Generator) screen
// of the Create Trivvy modal. Mirrors ShareTrivia/shareCopy.js: every new or
// reworded user-facing string lives here once so it is never duplicated inline.
// Strings that this restyle did not change stay where they are.

// ---- Name field ----
// The label is rendered uppercase via CSS (.bwai_label_caps) so the string
// itself stays readable; the asterisk is a separate node so it can be red.
export const BWAI_NAME_LABEL = 'Name'
export const BWAI_REQUIRED_MARK = '*'

// ---- Prompt instruction line ----
// Reproduces the original sentence verbatim, minus the literal "**" that used
// to leak into the rendered text. The lead is bolded via markup instead.
// The 40-character minimum belongs HERE, in the instruction the user reads
// before typing — it is also repeated in the tip popover (BWAI_TIP_MIN_CHARS),
// which is the discoverable "guidelines" surface.
// BWAI_PROMPT_TAIL intentionally has NO trailing period: the sentence ends
// inside BWAI_PROMPT_CHAR_LIMIT's parenthesis, exactly as it did before.
export const BWAI_PROMPT_LEAD = '**Tell us what you want to find out.'
export const BWAI_PROMPT_TAIL =
  'Please note you’ll get a better survey, the more specific you can be'
export const BWAI_PROMPT_CHAR_LIMIT =
  '(1000 characters max, no less than 40 characters.)'

// ---- Prompt helper row (tip + counter) ----
export const BWAI_TIP_TEXT =
  'Tip: start from example prompt or write your own based on our guidelines'
export const BWAI_TIP_MIN_CHARS_HEADING = 'Length'
export const BWAI_TIP_MIN_CHARS =
  ': your prompt must be at least 40 characters long, and no more than 1000.'

// ---- Number-of-questions field ----
// The cap is a separate node so it can carry its own weight/case (see Figma).
export const BWAI_QUESTIONS_LABEL = 'How many questions'
export const BWAI_QUESTIONS_CAP = '(up to 12)'

// ---- Sample prompt / sample message rows ----
// The action label each row carries. The old BuildAithAiCard put these in a
// CardFooter and it was the only thing telling the user what selecting a row
// does, so the wording is kept verbatim per screen.
export const BWAI_PROMPT_ACTION = 'Insert as prompt'
export const BWAI_SAMPLE_MESSAGE_ACTION = 'Insert Text'

// ---- Type picker (the modal's first screen) ----
// The redesign gives the picker a header subtitle, a section label and a
// select-then-Continue footer, so these strings are new. The three card
// descriptions are the design's own rewording of the old inline copy
// ("auto generate" -> "auto-generate", sentence-final periods).
export const BWAI_PICKER_TITLE = 'Create New Trivvy'
export const BWAI_PICKER_SUBTITLE =
  'Choose a starting point. You can customize everything later.'
export const BWAI_PICKER_SECTION_LABEL = 'Select a type'

export const BWAI_PICKER_SURVEY_TITLE = 'Survey Generator'
export const BWAI_PICKER_SURVEY_DESC =
  'Type in what you need to find out, and let AI generate your pulse survey.'
export const BWAI_PICKER_MESSAGING_TITLE = 'Messaging'
export const BWAI_PICKER_MESSAGING_DESC =
  'Send announcements, messages, alerts or any other type of communication, and get real-time analytics on responses.'
export const BWAI_PICKER_KC_TITLE = 'Knowledge Checks'
export const BWAI_PICKER_KC_DESC =
  'Use AI to auto-generate and schedule one or more assessments from any content in under a minute.'

export const BWAI_PICKER_MANUAL = 'Create manual survey'
export const BWAI_PICKER_NEVER_SHOW = 'Never show again'
export const BWAI_PICKER_CANCEL = 'Cancel'
export const BWAI_PICKER_CONTINUE = 'Continue'

// Header escape hatch on the Built with AI screen, shown only when the modal
// opened straight there from "+ Create Survey". Title case, per the ticket.
export const BWAI_HEADER_MANUAL_SURVEY = 'Create Manual Survey'

// ---- Privacy field (TBV-671) ----
// The privacy picker arrives from bug671, which inlined the same popover body
// at both of its call sites in CreateTriviaPopup. Centralized here so the two
// sites share one string instead of drifting apart.
export const BWAI_PRIVACY_LABEL = 'Privacy'
export const BWAI_PRIVACY_HELP =
  '"Only Me" keeps this Trivvy visible only to you (and admins with full access). "All Admins" lets every admin view it.'

/* ---- Messaging: how would you like to start? ---- */
/* The three modes replace the stacked Sample/AI accordions so only one section
   is on screen at a time, which is what removes the scrolling. */
export const BWAI_MSG_START_LABEL = 'How would you like to start?'
export const BWAI_MSG_MODE_AI = 'Generate with AI'
export const BWAI_MSG_MODE_AI_BADGE = 'Recommended'
export const BWAI_MSG_MODE_SAMPLE = 'Use a sample'
export const BWAI_MSG_MODE_WRITE = 'Write it myself'

export const BWAI_MSG_AI_TITLE = 'Instructions for AI'
export const BWAI_MSG_AI_DESC =
  'Tell AI what you want to communicate. These instructions are never sent to recipients.'
export const BWAI_MSG_AI_GENERATE = 'Generate Message'
export const BWAI_MSG_AI_GENERATING = 'Generating...'

export const BWAI_MSG_SAMPLE_HEADING = 'Choose a sample'
export const BWAI_MSG_CONTENT_LABEL = 'Message Content'
export const BWAI_MSG_SAMPLE_SELECTED = 'Sample selected:'
export const BWAI_MSG_SAMPLE_CHOOSE_ANOTHER = 'Choose another'
export const BWAI_MSG_AI_DRAFT_HINT = 'Your draft will appear below for review.'
export const BWAI_MSG_AI_BADGE = 'AI-generated'
export const BWAI_MSG_CONTENT_HINT =
  'Review and edit this message before creating it.'
export const BWAI_MSG_AI_UPDATE = 'Update Message'
